import { AuthSplit } from '@/components/layout/AuthSplit'
import { useEffect, useRef, useState } from 'react'
import { ArrowLeft, MailCheck, Send } from 'lucide-react'
import { Button, Field, Input } from '@/components/ui'
import { Turnstile, type TurnstileHandle } from '@/components/common/Turnstile'
import { api, describeError } from '@/services/api'
import { useAuthStore, useSettingsStore } from '@/store'

/**
 * "I have forgotten my password."
 *
 * The confirmation is deliberately the same whether or not the address has an
 * account. Telling somebody an email is not registered turns this form into a
 * free membership check for the church directory.
 */
export function ForgotPasswordPage({ onBack }: { onBack: () => void }) {
  const { signInConfig, loadSignInConfig } = useAuthStore()
  const theme = useSettingsStore((s) => s.settings?.theme)

  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const turnstile = useRef<TurnstileHandle | null>(null)

  const turnstileRequired = signInConfig?.turnstileEnabled === true

  useEffect(() => {
    if (!signInConfig) void loadSignInConfig()
  }, [signInConfig, loadSignInConfig])

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (turnstileRequired && !turnstileToken) {
      setError('Complete the verification first.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const result = await api.post<{ message: string }>('/auth/forgot', {
        email: email.trim(),
        turnstileToken,
      })
      setSent(result.message)
    } catch (sendError) {
      setError(describeError(sendError))
      turnstile.current?.reset()
      setTurnstileToken(null)
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthSplit heading="Reset your password">
      <>

        {sent ? (
          <div className="space-y-4 rounded-lg border border-border bg-card p-5 text-center">
            <MailCheck className="mx-auto h-8 w-8 text-status-confirmed" />
            <p className="text-sm">{sent}</p>
            {/*
              The MyMDV reason goes first, because since 2026-10-26 it is the
              likeliest one. Somebody who signs in here with their MyMDV
              password has no Services password at all, so /auth/forgot finds
              no hash, sends nothing, and — correctly, since it must answer the
              same way for an address it does not know — still says to check
              their email. Without this line that is a silent dead end.
            */}
            <p className="text-xs text-muted-foreground">
              Nothing arrived? If you sign in with your MyMDV password, reset that in MyMDV
              instead &mdash; this page only resets a separate Services password. Otherwise check
              the spam folder, or ask an administrator to set a new one for you.
            </p>
            <Button variant="outline" className="w-full" onClick={onBack}>
              <ArrowLeft className="h-4 w-4" />
              Back to sign in
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">
              Enter the address you sign in with and we will email you a link to choose a new
              password.
            </p>
            <p className="text-xs text-muted-foreground">
              This is for a separate Services password. A MyMDV password is reset in MyMDV.
            </p>

            <Field label="Email">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                required
                autoFocus
              />
            </Field>

            {turnstileRequired && signInConfig ? (
              <Turnstile
                siteKey={signInConfig.turnstileSiteKey}
                theme={theme === 'dark' ? 'dark' : theme === 'light' ? 'light' : 'auto'}
                onToken={setTurnstileToken}
                onError={setError}
                handleRef={(handle) => {
                  turnstile.current = handle
                }}
              />
            ) : null}

            {error ? (
              <p className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                {error}
              </p>
            ) : null}

            <Button
              type="submit"
              className="w-full"
              loading={busy}
              disabled={turnstileRequired && !turnstileToken}
            >
              <Send className="h-4 w-4" />
              Email me a link
            </Button>

            <button
              type="button"
              onClick={onBack}
              className="w-full text-center text-xs text-muted-foreground hover:text-foreground"
            >
              Back to sign in
            </button>
          </form>
        )}
      </>
    </AuthSplit>
  )
}
